// DEVAMS-2156: iOS Share Sheet receiver — Pinia store.
// Options API style to match project convention (see aircraftHandoverStore.ts, eventStore.ts).
//
// Flow:
//   1. iOS Share Extension writes file + metadata to shared App Group container.
//   2. Share Extension opens `aero.nayak.mro.jerkee://share-callback`.
//   3. App.vue receives the appUrlOpen event and calls loadPendingFile().
//   4. loadPendingFile() fetches the base64 file via AppGroupBridgePlugin and
//      sets pendingFile + pickerOpen = true.
//   5. ShareDestinationPicker.vue renders (controlled by pickerOpen) and lets
//      the user choose where to upload the file.
//   6. On upload success or cancel, clearPendingFile() resets state and removes
//      the file from the App Group container.

import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import jwtInterceptor from '@/plugins/jwtInterceptor'
import { AppGroupBridgePlugin } from '@/plugins/AppGroupBridgePlugin'

export interface PendingSharedFile {
  filename: string
  mimeType: string
  /** The file data as a Blob, ready to be appended to FormData. */
  data: Blob
  /** Destination type pre-selected in the Share Extension (event | task | handover-item). */
  destinationType?: string
}

export const useShareReceiverStore = defineStore('shareReceiver', {
  state: (): {
    pendingFile: PendingSharedFile | null
    pickerOpen: boolean
    uploading: boolean
    error: string | null
  } => ({
    pendingFile: null,
    pickerOpen: false,
    uploading: false,
    error: null,
  }),

  actions: {
    /**
     * Reads the pending file from the iOS App Group container via the native plugin.
     * Converts the base64-encoded payload into a Blob and opens the picker.
     * No-op when no pending file is present (non-iOS or empty container).
     */
    async loadPendingFile(): Promise<void> {
      try {
        const result = await AppGroupBridgePlugin.getPendingFile()

        if (!result.filename) {
          return
        }

        // Convert base64 → binary → Blob so it is ready for FormData.
        const binaryStr = atob(result.base64Data!)
        const bytes = new Uint8Array(binaryStr.length)
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i)
        }
        const blob = new Blob([bytes], { type: result.mimeType })

        this.pendingFile = {
          filename: result.filename,
          mimeType: result.mimeType!,
          data: blob,
          destinationType: result.destinationType,
        }
        this.pickerOpen = true
      } catch (error) {
        Sentry.captureException(error, {
          tags: { store: 'shareReceiver', action: 'loadPendingFile' },
        })
        console.error('[shareReceiver] loadPendingFile failed:', error)
      }
    },

    renameFile(filename: string): void {
      if (this.pendingFile) {
        this.pendingFile = { ...this.pendingFile, filename: filename.trim() || this.pendingFile.filename }
      }
    },

    /**
     * Removes the pending file from the App Group container and resets all state.
     * Must be called after successful upload or on picker cancel/dismiss.
     */
    async clearPendingFile(): Promise<void> {
      try {
        await AppGroupBridgePlugin.clearPendingFile()
      } catch (error) {
        Sentry.captureException(error, {
          tags: { store: 'shareReceiver', action: 'clearPendingFile' },
        })
      } finally {
        this.pendingFile = null
        this.pickerOpen = false
        this.error = null
      }
    },

    /**
     * Uploads the pending shared file to the selected destination.
     *
     * @param type         - Destination category.
     * @param id           - Primary entity ID (event id / task id / handover id).
     * @param itemId       - Handover item ID — required when type === 'handover-item'.
     *
     * Endpoints:
     *   event          → POST /api/v1/events/{id}/attachment                     (field: file)
     *   task           → POST /api/v1/plannedwork/task/{id}/attachments           (field: file)
     *   handover-item  → POST /api/v1/aircraft-handover/{id}/item/{itemId}/image  (field: image)
     */
    /**
     * Uploads the pending photo as a techrecord to a task.
     * Endpoint: POST v1/task/techrecords/{taskId}
     * Body: { image: "data:<mime>;base64,<data>", pageId: "<reference>" }
     */
    async uploadTechrecord(taskId: number, pageId: string): Promise<void> {
      if (!this.pendingFile) throw new Error('[shareReceiver] no pending file')
      this.uploading = true
      this.error = null
      try {
        const { mimeType, data } = this.pendingFile
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(data)
        })
        await jwtInterceptor.post(
          `v1/task/techrecords/${taskId}`,
          JSON.stringify({ image: base64, pageId }),
          { headers: { 'Content-Type': 'application/json' } },
        )
        await this.clearPendingFile()
      } catch (error) {
        this.error = 'Failed to upload techrecord. Please try again.'
        Sentry.captureException(error, {
          tags: { store: 'shareReceiver', action: 'uploadTechrecord', taskId },
        })
        throw error
      } finally {
        this.uploading = false
      }
    },

    async uploadToDestination(
      type: 'event' | 'task' | 'handover-item',
      id: number,
      itemId?: number,
    ): Promise<void> {
      if (!this.pendingFile) {
        throw new Error('[shareReceiver] uploadToDestination called with no pending file')
      }

      this.uploading = true
      this.error = null

      const { filename, mimeType, data } = this.pendingFile
      const formData = new FormData()

      try {
        if (type === 'handover-item') {
          if (itemId === undefined) {
            throw new Error('[shareReceiver] itemId is required for handover-item uploads')
          }
          // Handover item image endpoint uses field name "image"
          formData.append('image', data, filename)
          await jwtInterceptor.post(
            `v1/aircraft-handover/${id}/item/${itemId}/image`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          )
        } else {
          // Both event and task use field name "file"
          formData.append('file', data, filename)
          const url =
            type === 'event'
              ? `v1/events/${id}/attachment`
              : `v1/plannedwork/task/${id}/attachments`
          await jwtInterceptor.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }

        // Upload succeeded — clean up the staging file.
        await this.clearPendingFile()
      } catch (error) {
        this.error = 'Failed to upload file. Please try again.'
        Sentry.captureException(error, {
          tags: {
            store: 'shareReceiver',
            action: 'uploadToDestination',
            type,
            id,
            itemId: itemId ?? null,
            filename,
            mimeType,
          },
        })
        throw error
      } finally {
        this.uploading = false
      }
    },
  },
})
